// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useCallback, useState, useEffect } from 'react'
import { FormPrevious } from 'grommet-icons'
import { PageContent, Page, Anchor, Box } from 'grommet'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'

import { Layout } from '../../commoncomponents/layout/Layout'
import { ChatBot } from '../support-chat/ChatBot'
import VisibilityWrapper from '../../commoncomponents/visibility-wrapper/VisibilityWrapper'

import { CaseCreationContextProvider } from './creation/context/CaseCreationContext'
import CaseForm from './creation/components/CaseForm'
import CaseErrorPage from './creation/components/CaseErrorPage'
import CaseConfirmation from './creation/components/CaseConfirmation'

const CaseCreationPageContent = () => {
  const { t } = useTranslation(['support_hub'])
  const [caseData, setCaseData] = useState({})
  const [isCaseCreationSuccess, setIsCaseCreationSuccess] = useState(false)
  const [isCaseCreationForm, setIsCaseCreationForm] = useState(false)
  const [isError, setIsError] = useState(false)
  const navigate = useNavigate()
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      navigate('/support')
    }
  }

  useEffect(() => {
    setIsCaseCreationForm(true)
  }, [])

  const handleCaseCreation = useCallback(({ data, error }) => {
    if (data && data.caseNumber) {
      setCaseData(data)
      setIsCaseCreationSuccess(true)
      setIsCaseCreationForm(false)
      setIsError(false)
    } else if (error) {
      setCaseData(error)
      setIsCaseCreationSuccess(false)
      setIsCaseCreationForm(false)
      setIsError(true)
    }
  }, [])

  return (
    <>
      <Box
        direction="row"
        justify="start"
        pad={{ top: 'small', bottom: 'small', left: 'small' }}
      >
        <VisibilityWrapper hideFor={{ feature: 'glcp-support-home' }}>
          <Anchor
            data-testid="support-hub-link"
            icon={<FormPrevious color="brand" />}
            onClick={() => navigate('/support')}
            label={t('support_hub:support_title')}
            weight="normal"
            onKeyDown={handleKeyDown}
            tabIndex="0"
          />
        </VisibilityWrapper>
      </Box>
      <Page kind="narrow">
        <PageContent>
          <CaseCreationContextProvider>
            {isCaseCreationForm && (
              <CaseForm caseCreationCallback={handleCaseCreation} />
            )}
            {isCaseCreationSuccess && <CaseConfirmation caseData={caseData} />}
            {isError && <CaseErrorPage errorData={caseData} />}
          </CaseCreationContextProvider>
        </PageContent>
        <VisibilityWrapper hideFor={{ feature: 'glcp-support-chat' }}>
          <ChatBot />
        </VisibilityWrapper>
      </Page>
    </>
  )
}

const CaseCreationPage = () => {
  return (
    <Layout>
      <VisibilityWrapper
        hideFor={{
          feature: 'glcp-support-cases',
          deployment: ['COP', 'GLOP']
        }}
        fallbackComponent={<Navigate to="/support" />}
      >
        <CaseCreationPageContent />
      </VisibilityWrapper>
    </Layout>
  )
}

export default CaseCreationPage
